import { useNavigate } from "@tanstack/react-router";
import { Loader2, PackageOpen } from "lucide-react";
import ResponsiveImage from "../ResponsiveImage";
import { getTitle } from "../../helper/helper";
import { useActiveMenu } from "../../hooks/useActiveMenu";

const MAX_CATEGORY_TILES = 6;

export default function CategoryCatalogue() {
  const navigate = useNavigate();
  const { sections, menu } = useActiveMenu();
  const isLoading = !menu;

  const getImageObject = (section: any) => section?.image ?? undefined;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-20">
        <Loader2 className="animate-spin text-gray-400 w-8 h-8" />
      </div>
    );
  }

  const itemsToRender = sections.slice(0, MAX_CATEGORY_TILES);

  return (
    <section className="w-full py-8 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Heading */}
        <div className="mb-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 bg-clip-text text-transparent">
            Shop by Category
          </h2>
          <p className="text-sm sm:text-base text-gray-400 mt-1 font-medium italic">
            Explore our curated collections
          </p>
        </div>

        {itemsToRender.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
            <PackageOpen className="w-10 h-10 text-gray-300" />
            <p className="text-base font-semibold text-gray-700">
              No categories yet
            </p>
            <p className="text-sm text-gray-400">
              Categories will appear here once they are added.
            </p>
          </div>
        ) : (
          /* Grid Layout - 2 rows x 3 columns on desktop, 2 columns x 3 rows on mobile */
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {itemsToRender.map((section: any) => {
              const imageObject = getImageObject(section);
              const title = getTitle(
                section?.name || { translations: { en: "Category" } },
              );

              const handleCategoryClick = () => {
                navigate({
                  to: "/c/shop",
                  search: { category: title, search: undefined },
                });
              };

              return (
                <div
                  key={section?.sectionId}
                  className="relative group cursor-pointer"
                  onClick={handleCategoryClick}
                >
                  {/* Square Image Container */}
                  <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-gray-100 shadow-md transition-transform md:group-hover:scale-105">
                    <ResponsiveImage
                      imageObject={imageObject}
                      fallbackSrc="/icons/placeholder.webp"
                      sizes="(min-width: 768px) 33vw, 50vw"
                      alt={title}
                      className="absolute inset-0 h-full w-full"
                      style={{ height: "100%" }}
                    />

                    {/* Name Overlay - always visible on touch screens, hover-revealed from md up */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-3 md:pb-4">
                      <span className="text-white font-semibold text-sm sm:text-lg md:text-xl text-center leading-tight line-clamp-2 px-2">
                        {title}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
